/**
 * MEXC REST API Client
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/
 */

import type {
  MexcBookTickerRequest,
  MexcBookTickerResponse,
  MexcDepthRequest,
  MexcDepthResponse,
  MexcExchangeInfoRequest,
  MexcExchangeInfoResponse,
  MexcKlinesRequest,
  MexcKlinesResponse,
  MexcTicker24hRequest,
  MexcTicker24hResponse,
  MexcTradesRequest,
  MexcTradesResponse,
} from './types';

import { get } from '../../lib/http';
import type { RestRequestOptions } from '../../lib/types';

const BASE_URL = 'https://api.mexc.com';

/**
 * MEXC REST API endpoints.
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/
 */
export const mexcRestEndpoints = {
  exchangeInfo: `${BASE_URL}/api/v3/exchangeInfo`,
  depth: `${BASE_URL}/api/v3/depth`,
  klines: `${BASE_URL}/api/v3/klines`,
  trades: `${BASE_URL}/api/v3/trades`,
  ticker24h: `${BASE_URL}/api/v3/ticker/24hr`,
  bookTicker: `${BASE_URL}/api/v3/ticker/bookTicker`,
} as const;

// ========================================
// Helper Functions
// ========================================

/**
 * Format symbol for MEXC API calls.
 * MEXC uses uppercase concatenation: "BTCUSDT", "ETHUSDT".
 *
 * @example
 * formatMexcSymbol("btc", "usdt") // "BTCUSDT"
 */
export function formatMexcSymbol(base: string, quote: string): string {
  return `${base}${quote}`.toUpperCase();
}

/**
 * Parse MEXC symbol into base/quote.
 * Note: Requires known quote currencies as MEXC doesn't use separators.
 *
 * @example
 * parseMexcSymbol("BTCUSDT") // { base: "BTC", quote: "USDT" }
 */
export function parseMexcSymbol(
  symbol: string,
  knownQuotes: string[] = ['USDT', 'USDC', 'BTC', 'ETH', 'MX'],
): { base: string; quote: string } | null {
  const upperSymbol = symbol.toUpperCase();

  // Try each known quote currency (longest first to avoid false matches)
  const sortedQuotes = [...knownQuotes].sort((a, b) => b.length - a.length);

  for (const quote of sortedQuotes) {
    if (upperSymbol.endsWith(quote)) {
      const base = upperSymbol.slice(0, -quote.length);
      if (base.length > 0) {
        return { base, quote };
      }
    }
  }

  return null;
}

/**
 * Maps TradingView/widget resolution to MEXC kline interval for REST API.
 *
 * MEXC REST API supported intervals (exact enum values):
 * - 1m, 5m, 15m, 30m, 60m (minute-based - note: 60m NOT 1h!)
 * - 4h, 8h (hour-based - NO 2h, 6h, 12h!)
 * - 1d (day-based - NO 2d, 3d, 5d!)
 * - 1W, 1M (week/month - uppercase W!)
 *
 * @see https://www.mexc.com/api-docs/spot-v3/public-api-definitions#kline-interval
 *
 * @example
 * mapToMexcInterval('1') // '1m'
 * mapToMexcInterval('60') // '60m'
 * mapToMexcInterval('240') // '4h'
 * mapToMexcInterval('1D') // '1d'
 */
export function mapToMexcInterval(resolution: string): string {
  const upper = resolution.toUpperCase();

  // Handle day/week/month special cases
  if (upper === 'D' || upper === '1D') return '1d';
  if (upper === 'W' || upper === '1W') return '1W'; // uppercase W!
  if (upper === 'M' || upper === '1M') return '1M';

  // Parse numeric resolution (assumes minutes)
  const minutes = parseInt(resolution, 10);
  if (isNaN(minutes)) return '1m';

  // Exact matches for supported intervals
  if (minutes === 1) return '1m';
  if (minutes === 5) return '5m';
  if (minutes === 15) return '15m';
  if (minutes === 30) return '30m';
  if (minutes === 60) return '60m'; // IMPORTANT: 60m, NOT 1h!
  if (minutes === 240) return '4h';
  if (minutes === 480) return '8h';
  if (minutes === 1440) return '1d';
  if (minutes === 10080) return '1W'; // uppercase W!

  // For unsupported intervals, find closest supported one
  // MEXC intervals in minutes: 1, 5, 15, 30, 60, 240, 480, 1440, 10080
  const supportedMinutes = [1, 5, 15, 30, 60, 240, 480, 1440, 10080];
  const intervalNames: Record<number, string> = {
    1: '1m',
    5: '5m',
    15: '15m',
    30: '30m',
    60: '60m',
    240: '4h',
    480: '8h',
    1440: '1d',
    10080: '1W',
  };

  // Find closest supported interval
  let closest = supportedMinutes[0];
  let closestDiff = Math.abs(minutes - closest);

  for (const supported of supportedMinutes) {
    const diff = Math.abs(minutes - supported);
    if (diff < closestDiff) {
      closest = supported;
      closestDiff = diff;
    }
  }

  return intervalNames[closest];
}

// ========================================
// REST API Functions
// ========================================

/**
 * Exchange Information.
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/#exchange-information
 */
export const getMexcExchangeInfo = async (
  params: MexcExchangeInfoRequest,
  options?: RestRequestOptions,
): Promise<MexcExchangeInfoResponse> => {
  const normalizedParams: MexcExchangeInfoRequest = { ...params };

  if (Array.isArray(params.symbols)) {
    normalizedParams.symbols = params.symbols.join(',');
  }

  const response = await get<MexcExchangeInfoResponse, MexcExchangeInfoRequest>({
    url: mexcRestEndpoints.exchangeInfo,
    params: normalizedParams,
    options,
  });

  return response.data;
};

/**
 * Fetches orderbook depth (snapshot) for a symbol.
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/#order-book
 */
export const getMexcDepth = async (
  params: MexcDepthRequest,
  options?: RestRequestOptions,
): Promise<MexcDepthResponse> => {
  const response = await get<MexcDepthResponse, MexcDepthRequest>({
    url: mexcRestEndpoints.depth,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches historical klines (candlesticks) for a symbol.
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/#kline-candlestick-data
 */
export const getMexcKlines = async (
  params: MexcKlinesRequest,
  options?: RestRequestOptions,
): Promise<MexcKlinesResponse> => {
  const response = await get<MexcKlinesResponse, MexcKlinesRequest>({
    url: mexcRestEndpoints.klines,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches recent public trades for a symbol.
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/#recent-trades-list
 */
export const getMexcTrades = async (
  params: MexcTradesRequest,
  options?: RestRequestOptions,
): Promise<MexcTradesResponse> => {
  const response = await get<MexcTradesResponse, MexcTradesRequest>({
    url: mexcRestEndpoints.trades,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches 24-hour ticker statistics for a symbol.
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/#24hr-ticker-price-change-statistics
 */
export const getMexcTicker24h = async (
  params: MexcTicker24hRequest,
  options?: RestRequestOptions,
): Promise<MexcTicker24hResponse> => {
  const response = await get<MexcTicker24hResponse, MexcTicker24hRequest>({
    url: mexcRestEndpoints.ticker24h,
    params,
    options,
  });

  return response.data;
};

/**
 * Fetches best bid/ask prices and quantities.
 * @see https://mexcdevelop.github.io/apidocs/spot_v3_en/#symbol-order-book-ticker
 */
export const getMexcBookTicker = async (
  params: MexcBookTickerRequest,
  options?: RestRequestOptions,
): Promise<MexcBookTickerResponse> => {
  const response = await get<MexcBookTickerResponse, MexcBookTickerRequest>({
    url: mexcRestEndpoints.bookTicker,
    params,
    options,
  });

  return response.data;
};
